import * as React from "react";
import { ContentPageTheme } from "@/services/bedrock/dtos/ContentPageTheme";
import { StyleCell } from "./StyleBar";
import { ThemePickerPopover } from "./ThemePickerPopover";

export interface ThemeCellProps {
  themeId: number | null;
  themes: ContentPageTheme[];
  defaultThemeId: number | null;
  loading: boolean;
  disabled: boolean;
  /** The player's coach hasn't let them change branding, so the cell shows the theme but can't open. */
  locked?: boolean;
  onSelect: (themeId: number) => void;
}

/** Whether there's a choice to offer; with a single theme the cell isn't shown. */
export const hasThemeChoice = (themes: ContentPageTheme[], loading: boolean) => loading || themes.length > 1;

/** The theme's page color with its button color on top, the two colors players recognize it by. */
const ThemeSwatch: React.FC<{ theme: ContentPageTheme | undefined }> = ({ theme }) => (
  <span
    className="flex size-full items-center justify-center"
    style={{ backgroundColor: theme?.backgroundColor || "#f1f5f9" }}
  >
    <span
      className="size-3.5 rounded-full ring-2 ring-white/80"
      style={{ backgroundColor: theme?.primaryCtaColor || "#cbd5e1" }}
    />
  </span>
);

export const ThemeCell: React.FC<ThemeCellProps> = ({
  themeId,
  themes,
  defaultThemeId,
  loading,
  disabled,
  locked,
  onSelect,
}) => {
  const [open, setOpen] = React.useState(false);
  const theme = themes.find(t => t.id === themeId);

  return (
    <StyleCell
      label="Branding"
      value={loading || !theme ? "Your branding" : theme.name}
      preview={<ThemeSwatch theme={loading ? undefined : theme} />}
      disabled={disabled || loading}
      lockedReason={locked ? "Your admin manages the branding on your posts." : undefined}
      open={open}
      onOpenChange={setOpen}
      data-testid="theme-cell"
    >
      <ThemePickerPopover
        themes={themes}
        selectedId={themeId}
        defaultThemeId={defaultThemeId}
        onSelect={id => {
          setOpen(false);
          if (id !== themeId) onSelect(id);
        }}
      />
    </StyleCell>
  );
};
